/* eslint-disable @typescript-eslint/no-explicit-any */
import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {Location} from '@angular/common';
import {CanDeactivateFn, EventType, NavigationEnd, NavigationExtras, NavigationStart, Router, UrlTree} from '@angular/router';
import {Analytics, AnalyticsService} from "../analytics/analytics.service";
import {config} from "../../config";
import {AppReadyChecker} from "../app.init";
import {ReferralSourceService} from "./referral-source.service";

export type NavInfo = { page: FinderPage, section: Section.Finder } | { page: BookingPage, section: Section.Booking };

@Injectable({
  providedIn: 'root'
})
export class NavService {

  readonly #referral = inject(ReferralSourceService);

  /** Pages this visit never shows. Scheduling on behalf of the patient has no code step. */
  readonly #skippedPages = computed<ReadonlySet<string>>(() =>
    new Set(this.#referral.schedulingOnBehalfOf() ? [BookingPage.Verification] : []));

  #waits: Promise<void>[] = [];
  #routerState$ = signal<EventType>(EventType.NavigationEnd);
  readonly routerState = this.#routerState$.asReadonly();

  reset() {
    this.enteredFrom.set(Section.Finder);
  }

  addWait(promise: Promise<void>): void {
    this.#waits.push(promise);
  }

  allowBack = signal(true);

  enteredFrom = signal<Section>(Section.Finder);
  enteredWithBookingParams = signal(JSON.parse(sessionStorage.getItem('entryPoint:bookingParams') ?? 'false') as boolean ?? false);

  #origReferrer$ = signal<string>(sessionStorage.getItem('entryPoint:referrer') ?? document.referrer.trim());
  hasHitFind = signal(JSON.parse(sessionStorage.getItem('entryPoint:hitFind') ?? 'false') as boolean ?? false);

  totalStepCount = computed(() => {
    const enteredFrom = this.enteredFrom();
    let steps = this.#pageCount(Section.Booking);
    if (enteredFrom === Section.Finder) {
      steps += this.#pageCount(Section.Finder);
    }
    return steps;
  });

  #current$ = signal<NavInfo>({
    page: FinderPage.Location,
    section: Section.Finder,
  });

  readonly current = this.#current$.asReadonly();

  currentStep = computed(() => {
    let base = 0;
    if (this.enteredFrom() === Section.Booking) {
      base = Object.keys(PAGE_MAP[Section.Finder]).length;
    }
    const current = this.current();
    const currentSection = current.section;
    const currentPage = current.page;
    const pages = PAGE_MAP[currentSection];
    const skipped = this.#skippedPages();
    let step = 0;
    for (const key in pages) {
      if (key === currentPage) {
        break;
      }
      if (!skipped.has(key)) {
        step++;
      }
    }
    return base + step;
  });
  currentSection = computed(() => this.current().section);
  currentPage = computed(() => this.current().page);

  goingBack = signal(false);
  wentBack = signal(false);
  backLinkOrAction = computed(() => {
    const current = this.#current$();
    const allowBack = this.allowBack();
    const goingBack = this.goingBack();
    const referrer = this.#origReferrer$();
    const entryPoint = this.enteredFrom();

    if (!allowBack) {
      console.log('backLinkOrAction: back not allowed, returning null');
      return null;
    }
    if (current.page === BookingPage.Confirmation) {
      return null;
    }
    // const back = PAGE_MAP[current.section]![current.page]!.back;
    // console.log('back', back, current);
    // if (typeof back === 'string') {
    //   return `/${current.section}/${back}`;
    // }
    // if (!!back) {
    //   return `/${back.section}/${back.page}`;
    // }
    // if (this.#app.entryPoint() === 'landing') {
    //   return () => {
    //     // if (history.length > 0) {
    //     //   history.go(-history.length);
    //     // }
    //     history.back();
    //   };
    // }

    // if we're at the start and the referrer is ourselves then no back
    // console.log({referrer: document.referrer, usingReferrer: referrer});
    const isSelf = referrer.startsWith(location.origin) || referrer === '';
    if (isSelf) {

      const isFinderStart = current.section === Section.Finder && current.page === FinderPage.Location;
      const isBookingStart = current.section === Section.Booking && [BookingPage.Eligibility, BookingPage.Invalid, BookingPage.Loading].includes(current.page);

      if (entryPoint === Section.Finder && isFinderStart) {
        return null;
      }

      if (entryPoint === Section.Booking && isBookingStart) {
        return null;
      }
    }

    return () => {
      if (goingBack) {
        console.log('backLinkOrAction: going back, doing nothing');
        return;
      }
      this.goingBack.set(true);
      history.back();
    };
  });

  forwardLink = computed(() => {
    const current = this.#current$();
    const next = this.#adjacent(current, 'next');
    if (next === null) {
      return null;
    }
    return `/${next.section}/${next.page}`;
  });


  #location = inject(Location);

  #router = inject(Router);
  #analytics = inject(AnalyticsService);
  #appReady = inject(AppReadyChecker);

  constructor() {
    window.addEventListener('popstate', (event) => {

    });


    (window as any).nav = this;

    const docReferrer = document.referrer.trim();
    let usingReferrer = this.#origReferrer$();

    if (docReferrer.length && docReferrer.startsWith(config.endpoints.mainSite)) {
      this.#origReferrer$.set(docReferrer);
      usingReferrer = docReferrer;
    }

    effect(() => {
      const hasHitFind = this.hasHitFind();
      sessionStorage.setItem(`entryPoint:hitFind`, JSON.stringify(hasHitFind));
    });

    effect(() => {
      const enteredWithBookingParams = this.enteredWithBookingParams();
      sessionStorage.setItem(`entryPoint:bookingParams`, JSON.stringify(enteredWithBookingParams));
    });

    // a referral visit enters through a bridged booking link; restored here so a reload keeps it
    this.enteredFrom.set(usingReferrer.includes('/clinician') || this.#referral.isReferral() ? Section.Booking : Section.Finder);
    sessionStorage.setItem(`entryPoint:referrer`, usingReferrer);


    let hasEnteredAnyPage = false;
    let navStartCurrent = this.#current$();
    let navStartTarget: NavInfo|undefined;
    this.#router.events.subscribe((event) => {
      const wasGoingBack = this.goingBack();

      if (event.type === EventType.NavigationEnd || event.type === EventType.NavigationCancel || event.type === EventType.NavigationError) {
        this.goingBack.set(false);
      }

      if (event instanceof NavigationEnd) {
        this.wentBack.set(wasGoingBack);
        const url = event.url.substring(1);
        const parsed = this.parseFromUrl(url);
        const {section, page} = parsed ?? {section: Section.Finder, page: FinderPage.Location};


        hasEnteredAnyPage = true;
        //@ts-expect-error: Ts is getting confused by the union type. Could be resolved with guards but not worth the effort
        this.#current$.set({section, page});
        this.#analytics.fireEvent(page, Analytics.EventType.Page, {
          action: 'state',
          data: 'enter',
          specifier: section,
        });

      } else if (event instanceof NavigationStart) {
        // console.log('nav start', event.navigationTrigger, event.restoredState, event.url, event.id, event);
        if (!this.#appReady() || !hasEnteredAnyPage) {
          return;
        }
        // if (event.navigationTrigger !== 'popstate') {
        //
        // }
        const current = this.#current$();
        this.#analytics.fireEvent(current.page, Analytics.EventType.Page, {
          action: 'state',
          data: 'exit',
          specifier: current.section,
        });
      }
      if (event.type === EventType.NavigationEnd || event.type === EventType.NavigationStart) {
        if (event.type === EventType.NavigationStart) {
          navStartCurrent = this.#current$();
          navStartTarget = this.parseFromUrl(event.url.substring(1));
          const compare = this.compare(navStartTarget ?? navStartCurrent, navStartCurrent);
          console.log('nav start', {
            navStartCurrent,
            navStartTarget,
            current: this.#current$(),
            compare,
          });
          this.goingBack.set(compare === -1);
        } else {
          console.log('nav end', {
            navStartCurrent,
            navStartTarget,
            current: this.#current$(),
            wentBack: this.wentBack(),
            goingBack: this.goingBack(),
          });
        }
      }
      this.#routerState$.set(event.type);
    });

  }

  compare(a: NavInfo, b: NavInfo): -1 | 0 | 1 {
    if (a.section !== b.section) {
      return a.section === Section.Finder ? -1 : 1;
    }
    const pages = Object.keys(PAGE_MAP[a.section]);
    const aIndex = pages.indexOf(a.page);
    const bIndex = pages.indexOf(b.page);
    if (aIndex < bIndex) {
      return -1;
    } else if (aIndex > bIndex) {
      return 1;
    }
    return 0;
  }

  parseFromUrl(url: string): NavInfo | undefined {
    if (!url) {
      return undefined;
    }
    if (url.startsWith('/')) {
      url = url.substring(1);
    }
    const parts = url.split('?')[0].split('/');
    const section = parts[0]?.toLowerCase() as Section;
    let page = parts[1] as FinderPage | BookingPage;

    if (!(section in PAGE_MAP)) {
      return undefined;
    }

    if (!(page in PAGE_MAP[section])) {
      page = section === Section.Finder ? FinderPage.Location : BookingPage.Eligibility;
    }
    // @ts-expect-error: Ts is getting confused by the union type
    return {section, page};
  }

  async init() {
    const currentState = this.#location.getState() as NavState | null;
    let initOn = this.parseFromUrl(location.pathname.substring(1));
    if (!initOn) {
      initOn = {section: Section.Finder, page: FinderPage.Location};
    }

    if (!currentState?.depth) {

      this.#location.replaceState(this.buildUrlFor(initOn), undefined, {
        ...currentState ?? {},
        depth: 0,
        page: initOn.page,
      });
    }



    // if (initOn.section === Section.Finder) {
    //   this.enteredFrom.set(Section.Finder);
    //   await this.#router.navigate([Section.Finder, FinderPage.Location], {replaceUrl: true});
    // } else {
    //   this.enteredFrom.set(Section.Booking);
    //   await this.#router.navigate([Section.Booking, BookingPage.Eligibility], {replaceUrl: true});
    // }
  }

  async goto(section: Section.Finder, page: FinderPage, extras?: NavigationExtras): Promise<void>;
  async goto(section: Section.Booking, page: BookingPage, extras?: NavigationExtras): Promise<void>;
  async goto(section: Section, page: FinderPage | BookingPage, extras?: NavigationExtras): Promise<void> {

    const commands: unknown[] = [section];
    if (page.length) {
      commands.push(page);
    }
    const isBack = this.goingBack();

    extras = {
      ...extras ?? {},
      state: {
        ...this.#location.getState() ?? {},
        ...extras?.state ?? {},
        isForward: !isBack,
        isBack
      }
    }

    await this.#router.navigate(commands, {queryParamsHandling: 'merge', ...extras});
  }

  nextRouteState(page: FinderPage | BookingPage) {
    const current = this.#location.getState() as NavState | null;
    return {
      depth: (current?.depth ?? -1) + 1,
      page,
    };
  }

  async goForward(): Promise<void> {
    const next = this.#adjacent(this.#current$(), 'next');
    if (next === null) {
      return;
    }
    this.goingBack.set(false);
    return this.goto(next.section as any, next.page as any);
  }

  async goBack(): Promise<void> {
    const back = this.#adjacent(this.#current$(), 'back');
    if (back === null) {
      console.log('goBack: no back page found, staying on current page', this.#current$());
      return;
    }
    // the back action sets goingBack itself; setting it here first would make the action a no-op
    // return this.goto(back.section as any, back.page as any);
    return this.backLinkOrAction()?.call(this) ?? Promise.resolve();
  }

  /** The page before or after `from` in PAGE_MAP, stepping over pages this visit skips. */
  #adjacent(from: { section: Section, page: FinderPage | BookingPage }, direction: 'back' | 'next'): {
    section: Section,
    page: FinderPage | BookingPage
  } | null {
    const skipped = this.#skippedPages();
    let current = from;
    do {
      const target = PAGE_MAP[current.section]![current.page]![direction];
      if (target === null) {
        return null;
      }
      current = typeof target === 'string' ? {section: current.section, page: target} : target;
    } while (skipped.has(current.page));
    return current;
  }

  #pageCount(section: Section) {
    const skipped = this.#skippedPages();
    return Object.keys(PAGE_MAP[section]).filter(page => !skipped.has(page)).length;
  }

  buildUrlFor<TOpt extends { asTree?: boolean }>(page: FinderPage | BookingPage, opt?: TOpt): TOpt extends {
    asTree: true
  } ? UrlTree : string;
  buildUrlFor<TOpt extends { asTree?: boolean }>(pair: {
    readonly section: Section.Finder,
    readonly page: FinderPage
  } | {
    readonly section: Section.Booking,
    readonly page: BookingPage
  }, opt?: TOpt): TOpt extends { asTree: true } ? UrlTree : string;
  buildUrlFor<TOpt extends { asTree?: boolean }>(section: Section.Finder, page: FinderPage, opt?: TOpt): TOpt extends {
    asTree: true
  } ? UrlTree : string;
  buildUrlFor<TOpt extends {
    asTree?: boolean
  }>(section: Section.Booking, page: BookingPage, opt?: TOpt): TOpt extends {
    asTree: true
  } ? UrlTree : string;
  buildUrlFor<TOpt extends { asTree?: boolean }>(pairOrSectionOrPage: {
    readonly section: Section.Finder,
    readonly page: FinderPage
  } | {
    readonly section: Section.Booking,
    readonly page: BookingPage
  } | Section | FinderPage | BookingPage, pageOrOp?: FinderPage | BookingPage | TOpt, optFinal?: TOpt): TOpt extends {
    asTree: true
  } ? UrlTree : string {
    let opt: TOpt | undefined;
    let page: FinderPage | BookingPage | undefined;
    if (typeof pageOrOp === 'object') {
      opt = pageOrOp;
    } else {
      page = pageOrOp as FinderPage | BookingPage;
      opt = optFinal;
    }

    let pair: { section: Section, page: FinderPage | BookingPage }
    if (typeof pairOrSectionOrPage === 'object') {
      pair = pairOrSectionOrPage;
    } else {
      let section: Section;
      if (page === undefined) {
        page = pairOrSectionOrPage as FinderPage | BookingPage;
        section = page in PAGE_MAP.finder ? Section.Finder : Section.Booking;
      } else {
        section = pairOrSectionOrPage as Section;
      }
      pair = {section, page};
    }

    const url = `/${pair.section}/${pair.page}`;
    // @ts-expect-error: I've confused ts with the overloads
    return opt?.asTree ? this.#router.createUrlTree([url]) : url;
  }


  static readonly waitForBeforeNavHookGuard: CanDeactivateFn<unknown> = async () => {
    const nav = inject(NavService);
    const waits = nav.#waits;
    if (!waits.length) {
      return true;
    }
    nav.#waits = [];
    await Promise.all(waits);
    return true;
  };
}


export enum Section {
  Finder = 'finder',
  Booking = 'booking',
}

export enum FinderPage {
  Location = 'location',
  ClinicianType = 'clinician-type',
  Conditions = 'conditions',
  Age = 'age',
  VisitType = 'visit-type',
  Insurer = 'insurer',
  InsurancePlan = 'insurer-plan',
  Find = 'find',
}

export enum BookingPage {
  Loading = '',
  Invalid = 'invalid',
  Eligibility = 'eligibility',
  Details = 'details',
  GeneralInfo = 'general-info',
  ContactInfo = 'contact-info',
  Verification = 'verification',
  InsuranceDetails = 'insurance-details',
  InsuranceCards = 'insurance-cards',
  Confirmation = 'confirmation',
}


type PageMap = Record<Section, Partial<Record<FinderPage | BookingPage, {
  back: FinderPage | BookingPage | null | { section: Section, page: FinderPage },
  next: FinderPage | BookingPage | null | { section: Section, page: BookingPage },
}>>>

const PAGE_MAP: PageMap = {
  [Section.Finder]: {
    [FinderPage.Location]: {
      back: null,
      next: FinderPage.ClinicianType,
    },
    [FinderPage.ClinicianType]: {
      back: FinderPage.Location,
      next: FinderPage.Conditions,
    },
    [FinderPage.Conditions]: {
      back: FinderPage.ClinicianType,
      next: FinderPage.Age,
    },
    [FinderPage.Age]: {
      back: FinderPage.Conditions,
      next: FinderPage.VisitType,
    },
    [FinderPage.VisitType]: {
      back: FinderPage.Age,
      next: FinderPage.Insurer,
    },
    [FinderPage.Insurer]: {
      back: FinderPage.VisitType,
      next: FinderPage.InsurancePlan,
    },
    [FinderPage.InsurancePlan]: {
      back: FinderPage.Insurer,
      next: FinderPage.Find,
    },
    [FinderPage.Find]: {
      back: FinderPage.InsurancePlan,
      next: {
        section: Section.Booking,
        page: BookingPage.Eligibility,
      },
    }
  } as const,
  [Section.Booking]: {
    [BookingPage.Eligibility]: {
      back: {
        section: Section.Finder,
        page: FinderPage.Find,
      },
      next: BookingPage.Details,
    },
    [BookingPage.Details]: {
      back: BookingPage.Eligibility,
      next: BookingPage.GeneralInfo,
    },
    [BookingPage.GeneralInfo]: {
      back: BookingPage.Details,
      next: BookingPage.ContactInfo,
    },
    [BookingPage.ContactInfo]: {
      back: BookingPage.GeneralInfo,
      next: BookingPage.Verification,
    },
    [BookingPage.Verification]: {
      back: BookingPage.ContactInfo,
      next: BookingPage.InsuranceDetails,
    },
    [BookingPage.InsuranceDetails]: {
      back: BookingPage.Verification,
      next: BookingPage.InsuranceCards,
    },
    [BookingPage.InsuranceCards]: {
      back: BookingPage.InsuranceDetails,
      next: BookingPage.Confirmation,
    },
    [BookingPage.Confirmation]: {
      back: BookingPage.InsuranceCards,
      next: null,
    }
  },
} as const;


export interface NavState {
  depth: number;
  page: FinderPage | BookingPage;
}

